/**
 * What the organization's pages share (§5.25): who may do what on them, how a
 * refusal is read back, and the JSON editor the parts without a form of their
 * own are written in.
 *
 * Who may do what is asked here for the page's sake only. Every route checks
 * the membership again and answers 404 to anybody it does not hold — this
 * decides which buttons are drawn, never what they are allowed to do.
 */
import { useState } from 'react';
import type React from 'react';
import { WORKSPACE_SCHEMA, type DocConfig, type WorkspaceDocument } from '@airlock/contracts/workspace/document';
import { WORKSPACE_COLORS, workspaceColorAt } from '@airlock/contracts/workspace/vocabulary';
import { normalizeWorkspaceConfig } from '@airlock/contracts/settings/workspace-config';
import { DEFAULT_SETTINGS } from '@airlock/contracts/settings/defaults';
import { color, font, radius } from '../../theme/tokens';
import { ApiError } from '../../lib/api';
import type { Actor, Me, Organization } from '../../lib/types';

/** The organizations this account holds a seat in — the only ones whose projects it can read. */
export function seatOrgs(me: Me): Organization[] {
  return me.organizations.filter(
    (organization) => organization.role === 'owner' || organization.role === 'admin' || organization.role === 'member'
  );
}

/** The ones it administers: settings, audit, archiving and reverting are theirs. */
export function adminOrgs(me: Me): Organization[] {
  return me.organizations.filter((organization) => organization.role === 'owner' || organization.role === 'admin');
}

export function administers(me: Me, orgId: string): boolean {
  const role = me.organizations.find((organization) => organization.id === orgId)?.role;
  return role === 'owner' || role === 'admin';
}

export function failureText(failure: unknown, fallback = 'That did not work.'): string {
  return failure instanceof ApiError ? failure.message : fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Every problem a 422 named, by path — or `null` when the failure was something else. */
export function refusalProblems(failure: unknown): string[] | null {
  if (!(failure instanceof ApiError) || failure.status !== 422 || !isRecord(failure.body)) return null;
  const problems = failure.body.problems;
  return Array.isArray(problems) ? problems.filter((problem): problem is string => typeof problem === 'string') : null;
}

/** The body of a 409, typed as the route says it is, or `null`. */
export function conflictOf<T>(failure: unknown): T | null {
  return failure instanceof ApiError && failure.status === 409 && isRecord(failure.body) ? (failure.body as T) : null;
}

/**
 * The problems about one part of a document. A problem reads `path: sentence`,
 * and the path is spelled the way `parseWorkspaceDocument` spells it —
 * `repos[api].env[TOKEN]`, `config.vmCpu` — so a section claims its own by prefix.
 */
export function problemsUnder(problems: string[], prefix: string): string[] {
  return problems.filter((problem) => problem === prefix || problem.startsWith(`${prefix}.`) || problem.startsWith(`${prefix}[`) || problem.startsWith(`${prefix}:`));
}

export function ProblemList({ problems }: { problems: string[] }): React.JSX.Element | null {
  if (problems.length === 0) return null;
  return (
    <ul
      style={{
        margin: '12px 0 0',
        padding: '12px 14px 12px 30px',
        background: color.badSoft,
        border: `1px solid ${color.badLine}`,
        borderRadius: radius.lg,
        fontSize: 13,
        lineHeight: 1.6,
        color: color.badText
      }}
    >
      {problems.map((problem, index) => {
        const split = problem.indexOf(': ');
        return (
          <li key={index}>
            {split > 0 ? (
              <>
                <span style={{ fontFamily: font.mono, fontSize: 12 }}>{problem.slice(0, split)}</span>
                {problem.slice(split)}
              </>
            ) : (
              problem
            )}
          </li>
        );
      })}
    </ul>
  );
}

export const textareaStyle: React.CSSProperties = {
  width: '100%',
  fontFamily: font.mono,
  fontSize: 12.5,
  lineHeight: 1.55,
  padding: '11px 12px',
  border: `1px solid ${color.lineInput}`,
  borderRadius: radius.lg,
  background: color.page,
  color: color.ink,
  resize: 'vertical'
};

export const selectStyle: React.CSSProperties = {
  width: '100%',
  fontFamily: 'inherit',
  fontSize: 14,
  padding: '11px 12px',
  border: `1px solid ${color.lineInput}`,
  borderRadius: radius.lg,
  background: color.page,
  color: color.ink
};

/**
 * A part of a document edited as JSON — the configuration, or a repository's
 * console (§5.25 says raw JSON is v1's editor for these). The text is the
 * person's; the value handed up is what it parses to, and only when it parses,
 * so a half-typed brace never reaches the document. The server still refuses
 * what the app would, by path.
 *
 * Remount it with a `key` to replace what is being edited — after a reload, a
 * text somebody typed is theirs until then.
 */
export function JsonField({
  id,
  label,
  hint,
  value,
  rows = 8,
  onValue,
  onValidity
}: {
  id: string;
  label: string;
  hint?: React.ReactNode;
  value: unknown;
  rows?: number;
  onValue: (next: unknown) => void;
  onValidity?: (valid: boolean) => void;
}): React.JSX.Element {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [invalid, setInvalid] = useState<string | null>(null);

  const change = (next: string): void => {
    setText(next);
    try {
      const parsed: unknown = JSON.parse(next);
      setInvalid(null);
      onValidity?.(true);
      onValue(parsed);
    } catch (error) {
      setInvalid(error instanceof Error ? error.message : 'That is not JSON.');
      onValidity?.(false);
    }
  };

  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', fontSize: 12.5, color: color.inkMuted, marginBottom: 6 }}>
        {label}
      </label>
      <textarea
        id={id}
        spellCheck={false}
        rows={rows}
        value={text}
        onChange={(event) => change(event.target.value)}
        style={{ ...textareaStyle, borderColor: invalid ? color.badLine : color.lineInput }}
      />
      {invalid ? (
        <div style={{ fontSize: 12.5, color: color.badText, marginTop: 6 }}>Not saved until it parses — {invalid}</div>
      ) : hint ? (
        <div style={{ fontSize: 12.5, color: color.inkFaint, marginTop: 6, lineHeight: 1.55 }}>{hint}</div>
      ) : null}
    </div>
  );
}

/** Which organization a page is about, where the account is in more than one. */
export function OrgPicker({
  orgs,
  value,
  onChange
}: {
  orgs: Organization[];
  value: string;
  onChange: (orgId: string) => void;
}): React.JSX.Element | null {
  if (orgs.length < 2) return null;
  return (
    <select
      aria-label="Organization"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      style={{ ...selectStyle, width: 'auto', fontSize: 13.5, padding: '8px 11px', borderRadius: radius.md }}
    >
      {orgs.map((organization) => (
        <option key={organization.id} value={organization.id}>
          {organization.name}
        </option>
      ))}
    </select>
  );
}

export function actorLabel(actor: Actor | null | undefined): string {
  return actor?.label ?? 'somebody';
}

/** A moment in a trail: the day and the minute, in UTC like every other date here. */
export function stamp(at: string | null | undefined): string {
  if (!at) return '—';
  const date = new Date(at.includes('T') ? at : `${at.replace(' ', 'T')}Z`);
  return Number.isNaN(date.getTime()) ? at : `${date.toISOString().slice(0, 10)} ${date.toISOString().slice(11, 16)}`;
}

/** An id of the shape the desktop app mints — so a row made here merges by id with one made there. */
export function newRowId(prefix: string): string {
  return `${prefix}_${crypto.randomUUID().replace(/-/g, '').slice(0, 8)}`;
}

/**
 * A configuration that overrides nothing: every member's install keeps its own
 * values. What the *rows* of a settings page are read against.
 */
export function emptyConfig(): DocConfig {
  const { workspaceId: _workspace, ...config } = normalizeWorkspaceConfig('new', {});
  return config;
}

/**
 * The configuration a workspace started here is **born holding**: Airlock's own
 * value for every setting, written down.
 *
 * A new workspace used to be born overriding nothing, so its page drew every
 * control on a value that was nowhere in the document — and a member's machine
 * went on using its own. The page said `Opus · X-High` and the session ran
 * Haiku, both correctly, about two different layers. What is shown here is now
 * a value that was actually published, and `Reset` on a row is how it is handed
 * back to each machine.
 *
 * The three lists keep their empty arrays: nothing is the right default for an
 * allowlist, a blocklist and the services a machine opens (§7.1, §5.9).
 */
export function startingConfig(): DocConfig {
  const app = DEFAULT_SETTINGS as unknown as Record<string, unknown>;
  const config = emptyConfig() as unknown as Record<string, unknown>;
  for (const key of Object.keys(config)) {
    if (Array.isArray(config[key])) continue;
    if (app[key] !== undefined) config[key] = app[key];
  }
  return config as unknown as DocConfig;
}

export function emptyDocument(): WorkspaceDocument {
  return {
    schema: WORKSPACE_SCHEMA,
    name: '',
    color: workspaceColorAt(0),
    repos: [],
    config: startingConfig(),
    workflows: [],
    taskSources: []
  };
}

/**
 * The colour a workspace or a project is marked with — the app's six, as
 * swatches.
 *
 * A field to type a colour into was the wrong control twice over: what belongs
 * there is one of six values written in `oklch()`, and what was typed there was
 * a hex the app's own picker could then not show as chosen. So the same six the
 * app offers, in the same order, and the value is the swatch.
 *
 * A colour that is not among them — written before this list, or by hand — is
 * shown at the end and marked as chosen, because a picker that silently dropped
 * it would change a workspace nobody asked to change.
 */
export function ColorPicker({
  value,
  onChange,
  disabled,
  label
}: {
  value: string;
  onChange: (color: string) => void;
  disabled?: boolean;
  /** What a reader of the page hears on the group — "Colour of api". */
  label: string;
}): React.JSX.Element {
  const own = value && !WORKSPACE_COLORS.includes(value) ? [value] : [];
  return (
    <div role="radiogroup" aria-label={label} style={{ display: 'flex', alignItems: 'center', gap: 9, height: 42 }}>
      {[...WORKSPACE_COLORS, ...own].map((swatch) => {
        const chosen = swatch === value;
        return (
          <button
            key={swatch}
            type="button"
            role="radio"
            aria-checked={chosen}
            aria-label={swatch}
            disabled={disabled}
            onClick={() => onChange(swatch)}
            style={{
              width: 22,
              height: 22,
              padding: 0,
              border: 0,
              borderRadius: radius.chip,
              background: swatch,
              // Chosen is a ring around it, not a bigger dot: the swatch is the
              // value, so nothing should change its shape.
              boxShadow: chosen ? `0 0 0 2px ${color.page}, 0 0 0 3.5px ${color.ink}` : 'none',
              cursor: disabled ? 'default' : 'pointer'
            }}
          />
        );
      })}
    </div>
  );
}

/** A section's heading inside a card. */
export function SectionTitle({ children, aside }: { children: React.ReactNode; aside?: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
      <div style={{ fontSize: 16, fontWeight: 600 }}>{children}</div>
      <span style={{ flex: 1 }} />
      {aside}
    </div>
  );
}
